import { Formik } from "formik";
import moment from "moment";
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import NumberFormat from "react-number-format";

import { INITIAL_DATA_FORM_ANIMAL_MEASUREMENT } from "../../../contexts/animalMeasurementContext";
import useAnimalMeasurementContext from "../../../hooks/useAnimalMeasurementContext";

import { Col, DatePicker, Divider, Input, Row, Select, Spin, notification } from "antd";
import ButtonStandard from "../../utils/button";
import { Body, Container, Footer } from "./styles";

import {
  getLastAnimalMeasurement,
  saveAnimalMeasurement,
  updateAnimalMeasurement,
} from "../../../services/animalMeasurementService";
import { indexAll as indexAllMeasurements } from "../../../services/measurementService";
import { numberMask } from "../../../services/helpersMethodsService";
import SearchAnimal from "../../utils/searchAnimal";
import SelectLotProduction from "../../utils/selectLotProduction";

function DrawerNewAnimalMeasurement() {
  const formikRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [animalSelected, setAnimalSelected] = useState(null);
  const [lastMeasurement, setLastMeasurement] = useState(null);
  const [form, setForm] = useState(INITIAL_DATA_FORM_ANIMAL_MEASUREMENT);

  const {
    tableProperties: { size, page, search },
    fetchData,
    showDrawerForm,
    openOrCloseForm,
    data,
  } = useAnimalMeasurementContext();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const validationSchema = Yup.object().shape({
    measurementDate: Yup.string().required(),
    animalId: Yup.string().required(),
    measurementId: Yup.string().required(),
    value: Yup.number().max(9999.99).required(),
  });

  const closeDrawer = useCallback(() => {
    formikRef.current.resetForm(INITIAL_DATA_FORM_ANIMAL_MEASUREMENT);
    setAnimalSelected(null);
    setLastMeasurement(null);
    openOrCloseForm();
  }, [openOrCloseForm]);

  const fetchMeasurementTypes = useCallback(async () => {
    try {
      const {
        data: { results },
      } = await indexAllMeasurements({ groupId, farmId });
      setMeasurementTypes(
        (results || []).filter((type) => type.status === "Active")
      );
    } catch (error) {
      console.error(error);
    }
  }, [groupId, farmId]);

  const fetchLastMeasurement = useCallback(
    async (animalId, measurementId) => {
      if (!animalId || !measurementId) {
        setLastMeasurement(null);
        return;
      }
      try {
        const {
          data: { results },
        } = await getLastAnimalMeasurement({
          groupId,
          farmId,
          animalId,
          measurementId,
        });
        setLastMeasurement(results);
      } catch (error) {
        setLastMeasurement(null);
      }
    },
    [groupId, farmId]
  );

  const selectAnimal = useCallback(
    (id, animal, searchField, setFieldValue, measurementId) => {
      if (animal) {
        setAnimalSelected(animal);
        setFieldValue("animalId", id);
        if (searchField === "handlingNumber") {
          setFieldValue("handlingNumber", animal.handlingNumber);
        } else if (searchField === "sisbov") {
          setFieldValue("handlingNumber", animal.sisbov);
        } else if (searchField === "tagId") {
          setFieldValue("handlingNumber", animal.tagId);
        } else if (searchField === "nickname") {
          setFieldValue("handlingNumber", animal.nickname);
        }
        fetchLastMeasurement(id, measurementId);
      }
    },
    [fetchLastMeasurement]
  );

  const handleDeselectAnimal = useCallback((setFieldValue) => {
    setAnimalSelected(null);
    setLastMeasurement(null);
    setFieldValue("animalId", null);
    setFieldValue("handlingNumber", null);
  }, []);

  const handleDrawerOpen = (visible) => {
    if (visible === true) {
      fetchMeasurementTypes();
      setForm(data);
      formikRef.current.setValues(data);
    }
  };

  const handleSubmitModalForm = useCallback(
    async (values, actions) => {
      setIsLoading(true);
      try {
        const body = {
          animalId: values.animalId,
          measurementId: values.measurementId,
          value: values.value,
          measurementDate: moment(values.measurementDate).format(
            "YYYY-MM-DD HH:mm:ss"
          ),
          lotDestinationId: values.lotDestinationId,
        };
        if (values.id != null) {
          await updateAnimalMeasurement({
            groupId,
            farmId,
            id: values.id,
            body,
          });
          notification.success({
            message: translation.animalMeasurement.form.messages.successMessage,
          });
          fetchData(page, size, {}, search);
          closeDrawer();
        } else {
          await saveAnimalMeasurement({ groupId, farmId, body });
          notification.success({
            message: translation.animalMeasurement.form.messages.successMessage,
          });
          fetchData(page, size, {}, search);
          if (values.saveOptions === "save_and_add_new") {
            actions.resetForm({
              ...INITIAL_DATA_FORM_ANIMAL_MEASUREMENT,
              measurementDate: values.measurementDate,
              lotDestinationId: values.lotDestinationId,
              saveOptions: "save_and_add_new",
            });
            setAnimalSelected(null);
            setLastMeasurement(null);
          } else {
            closeDrawer();
          }
        }
      } catch (error) {
        console.error("error", error);
        notification.error({
          message: translation.animalMeasurement.form.messages.errorMessage,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      closeDrawer,
      farmId,
      fetchData,
      groupId,
      page,
      search,
      size,
      translation.animalMeasurement.form.messages.errorMessage,
      translation.animalMeasurement.form.messages.successMessage,
    ]
  );

  return (
    <Container
      title={
        data.id != null
          ? translation.animalMeasurement.form.titleEdit
          : translation.animalMeasurement.form.titleCreateNew
      }
      width={770}
      onClose={closeDrawer}
      maskClosable={false}
      visible={showDrawerForm}
      afterVisibleChange={handleDrawerOpen}
    >
      <Formik
        ref={formikRef}
        enableReinitialize={true}
        initialValues={form}
        onSubmit={handleSubmitModalForm}
        validationSchema={validationSchema}
        render={({
          values,
          errors,
          setFieldValue,
          submitCount,
          resetForm,
          handleSubmit,
        }) => (
          <Spin spinning={isLoading}>
            <form onSubmit={handleSubmit} autoComplete="off">
              <Body>
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}
                <Row type="flex" gutter={8}>
                  <Col span={8}>
                    <Row>
                      <label
                        className={
                          errors.measurementDate && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.animalMeasurement.form.date}*
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <DatePicker
                        autoFocus={true}
                        format="DD/MM/YYYY"
                        disabled={values.id != null}
                        value={
                          values.measurementDate
                            ? moment(values.measurementDate)
                            : undefined
                        }
                        placeholder={translation.defaultDatePickerPlaceholder}
                        disabledDate={(current) =>
                          animalSelected != null
                            ? current.isAfter(moment()) ||
                              current.isBefore(moment(animalSelected?.birthday))
                            : current.isAfter(moment())
                        }
                        onChange={(date) => setFieldValue("measurementDate", date)}
                      />
                    </Row>
                  </Col>
                  <Col span={16}>
                    <SearchAnimal
                      textSearch={values.handlingNumber ?? undefined}
                      setTextSearch={(value) =>
                        setFieldValue("handlingNumber", value)
                      }
                      value={values.animalId}
                      handleChange={(id, animal, searchField) =>
                        selectAnimal(
                          id,
                          animal,
                          searchField,
                          setFieldValue,
                          values.measurementId
                        )
                      }
                      handleDeselect={() => handleDeselectAnimal(setFieldValue)}
                      showDeselect
                      hasError={
                        (errors.handlingNumber || errors.animalId) &&
                        submitCount > 0
                      }
                    />
                  </Col>
                </Row>
                <Row type="flex" gutter={8}>
                  <Col span={8}>
                    <Row>
                      <label
                        className={
                          errors.measurementId && submitCount > 0 ? "error" : ""
                        }
                      >
                        {translation.animalMeasurement.form.measurement}*
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <Select
                        value={values.measurementId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => {
                          setFieldValue("measurementId", value);
                          fetchLastMeasurement(values.animalId, value);
                        }}
                      >
                        {measurementTypes.map((type) => (
                          <Select.Option key={type.id} value={type.id}>
                            {type.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                  <Col span={8}>
                    <Row>
                      <label
                        className={
                          errors.value && submitCount > 0 ? "error" : ""
                        }
                      >
                        {translation.animalMeasurement.form.value}*
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        customInput={Input}
                        value={values.value ?? ""}
                        placeholder={translation.defaultPlaceholder}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        allowNegative={false}
                        isAllowed={({ floatValue }) =>
                          floatValue === undefined || floatValue <= 9999.99
                        }
                        onValueChange={({ floatValue }) =>
                          setFieldValue("value", floatValue ?? null)
                        }
                      />
                    </Row>
                  </Col>
                  <Col span={8}>
                    <Row>
                      <label>
                        {translation.animalMeasurement.form.currentLot}
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <strong>{animalSelected?.lotName ?? "-"}</strong>
                    </Row>
                  </Col>
                </Row>
                <Row type="flex" gutter={8}>
                  <Col span={8}>
                    <Row>
                      <label>{translation.animalMeasurement.form.gender}</label>
                    </Row>
                    <Row className="rowInput">
                      <strong>
                        {animalSelected?.gender
                          ? translation.animalMeasurement.gender[
                              animalSelected.gender
                            ] ?? animalSelected.gender
                          : "-"}
                      </strong>
                    </Row>
                  </Col>
                  <Col span={8}>
                    <Row>
                      <label>{translation.animalMeasurement.form.breed}</label>
                    </Row>
                    <Row className="rowInput">
                      <strong>{animalSelected?.breedName ?? "-"}</strong>
                    </Row>
                  </Col>
                  {values.id === null && (
                    <Col span={8}>
                      <Row>
                        <label>
                          {translation.animalMeasurement.form.lotDestination}
                        </label>
                      </Row>
                      <Row className="rowInput">
                        <SelectLotProduction
                          value={values.lotDestinationId}
                          onDeselect={() =>
                            setFieldValue("lotDestinationId", null)
                          }
                          onChange={(value) =>
                            setFieldValue("lotDestinationId", value)
                          }
                        />
                      </Row>
                    </Col>
                  )}
                </Row>
                {values.id === null &&
                  values.animalId != null &&
                  values.measurementId != null && (
                  <>
                    <Divider dashed>
                      {translation.animalMeasurement.form.lastMeasurement}
                    </Divider>
                    <Row type="flex" gutter={8}>
                      <Col span={12}>
                        <Row>
                          <label>
                            {translation.animalMeasurement.form.lastDate}
                          </label>
                        </Row>
                        <Row>
                          <strong>
                            {lastMeasurement?.measurementDate
                              ? moment(lastMeasurement.measurementDate).format(
                                  "DD/MM/YYYY"
                                )
                              : "-"}
                          </strong>
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row>
                          <label>
                            {translation.animalMeasurement.form.lastValue}
                          </label>
                        </Row>
                        <Row>
                          <strong>
                            {lastMeasurement?.value != null
                              ? numberMask(Number(lastMeasurement.value), false)
                              : "-"}
                          </strong>
                        </Row>
                      </Col>
                    </Row>
                  </>
                )}
              </Body>
              <Footer>
                <Row type="flex" justify="space-between">
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="selectDiv"
                  >
                    {values.id === null && (
                      <Select
                        value={values.saveOptions}
                        name="saveOptions"
                        onChange={(value) =>
                          setFieldValue("saveOptions", value)
                        }
                      >
                        <Select.Option key="1" value="only_save">
                          {translation.animalMeasurement.form.saveOptions.onlySave}
                        </Select.Option>
                        <Select.Option key="2" value="save_and_add_new">
                          {
                            translation.animalMeasurement.form.saveOptions
                              .saveAndAddNew
                          }
                        </Select.Option>
                      </Select>
                    )}
                  </Col>
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="buttonsDiv"
                  >
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={() => closeDrawer(resetForm)}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </form>
          </Spin>
        )}
      />
    </Container>
  );
}

export default DrawerNewAnimalMeasurement;
